import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FilterConfig, GlobalFilterPanelComponent } from '../components/global-filter-panel/global-filter-panel.component';
import { DashboardService } from '../dashboard.service';
import { DashboardEngineService } from '../services/dashboard-engine.service';
import { ActivityTimelineComponent } from '../widgets/shared/activity-timeline/activity-timeline.component';
import { ApprovalQueueListComponent } from '../widgets/shared/approval-queue-list/approval-queue-list.component';
import { ComplianceTrackerComponent } from '../widgets/shared/compliance-tracker/compliance-tracker.component';
import { FinancialSummaryCardComponent } from '../widgets/shared/financial-summary-card/financial-summary-card.component';
import { KpiCard, KpiCardGridComponent } from '../widgets/shared/kpi-card-grid/kpi-card-grid.component';
import { PerformanceBenchmarkComponent } from '../widgets/shared/performance-benchmark/performance-benchmark.component';
import { RiskRegisterComponent } from '../widgets/shared/risk-register/risk-register.component';
import { SubscriptionBillingCardComponent } from '../widgets/shared/subscription-billing-card/subscription-billing-card.component';
import { VmsActivityCardComponent } from '../widgets/shared/vms-activity-card/vms-activity-card.component';
import { WorkOrderExpiryTrackerComponent } from '../widgets/shared/work-order-expiry-tracker/work-order-expiry-tracker.component';
import { WorkQueueListComponent } from '../widgets/shared/work-queue-list/work-queue-list.component';

@Component({
  selector: 'app-executive-dashboard',
  standalone: true,
  imports: [
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    GlobalFilterPanelComponent,
    KpiCardGridComponent,
    WorkQueueListComponent,
    ApprovalQueueListComponent,
    RiskRegisterComponent,
    PerformanceBenchmarkComponent,
    WorkOrderExpiryTrackerComponent,
    ComplianceTrackerComponent,
    ActivityTimelineComponent,
    FinancialSummaryCardComponent,
    SubscriptionBillingCardComponent,
    VmsActivityCardComponent,
  ],
  templateUrl: './executive-dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { style: 'display: block; width: 100%;' },
})
export class ExecutiveDashboardComponent {
  private engine = inject(DashboardEngineService);
  protected dashboardSvc = inject(DashboardService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  readonly dashboardType = 'executive';
  filters: any = {};

  portfolioCards: KpiCard[] = [];
  loadingPortfolio = false;

  readonly filterConfig: FilterConfig = {
    community: true,
    period: true,
    dateRange: false,
  };

  onFiltersChanged(f: any): void {
    this.filters = { ...f };
    this.dashboardSvc.activeFilters.set(this.filters);
    this.loadPortfolioSummary();
    this.cdr.markForCheck();
  }

  private loadPortfolioSummary(): void {
    this.loadingPortfolio = true;
    this.engine
      .getWidgetData(this.dashboardType, 'portfolioSummary', this.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          const d = res.record?.data ?? {};
          this.portfolioCards = [
            { label: 'Active Communities', value: d.communities ?? 0, icon: 'location_city', color: 'text-blue-600', bgColor: 'bg-blue-50', route: '/associations' },
            { label: 'Active Units', value: d.units ?? 0, icon: 'door_front', color: 'text-violet-600', bgColor: 'bg-violet-50' },
            { label: 'Active Owners', value: d.owners ?? 0, icon: 'person', color: 'text-emerald-600', bgColor: 'bg-emerald-50', route: '/directories/owners' },
            { label: 'Active Residents', value: d.residents ?? 0, icon: 'group', color: 'text-teal-600', bgColor: 'bg-teal-50', route: '/directories/residents' },
            { label: 'Active Vendors', value: d.vendors ?? 0, icon: 'handshake', color: 'text-orange-600', bgColor: 'bg-orange-50', route: '/directories/vendors' },
            { label: 'Active Users', value: d.active_users ?? 0, icon: 'manage_accounts', color: 'text-pink-600', bgColor: 'bg-pink-50', route: '/admin/users' },
            {
              label: 'Pending Requests',
              value: d.pending_requests ?? 0,
              icon: 'support_agent',
              color: 'text-amber-600',
              bgColor: 'bg-amber-50',
              route: '/eservices/list',
              queryParams: { status: 'Pending' },
            },
          ];
          this.loadingPortfolio = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadingPortfolio = false;
          this.cdr.markForCheck();
        },
      });
  }
}
